import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo, { localBusinessSchema } from "@/components/Seo";
import Reveal from "@/components/Reveal";
import CtaBand from "@/components/CtaBand";
import { townBySlug, SITE, SERVICES } from "@/data/site";

export default function LocationPage() {
  const { slug } = useParams();
  const t = townBySlug(slug);
  if (!t) return <Navigate to="/" replace />;

  return (
    <>
      <Seo
        title={`Therapist for ${t.town}, CT | Let Go Wellness`}
        description={`Looking for a therapist near ${t.town}, CT? Let Go Wellness in Waterbury offers anxiety, depression, couples and family therapy — ${t.drive} of ${t.town}, plus telehealth.`}
        path={`/areas/${t.slug}`}
        jsonLd={localBusinessSchema}
      />
      <section className="border-b border-border">
        <div className="mx-auto max-w-4xl px-6 py-20 md:py-28">
          <Reveal>
            <div className="overline mb-6">Serving {t.town}, Connecticut</div>
            <h1 className="font-serif text-5xl leading-tight text-ink sm:text-6xl">Therapy for <span className="italic text-clay">{t.town}, CT</span></h1>
            <p className="mt-8 max-w-2xl font-sans text-lg leading-relaxed text-ink-soft">
              Let Go Wellness is a short drive from {t.town} — our Waterbury clinic sits {t.drive} on Highland Avenue. We offer compassionate, evidence-based mental health therapy in person, plus secure telehealth for {t.town} residents anywhere in Connecticut.
            </p>
            <p className="mt-4 max-w-2xl font-sans text-base leading-relaxed text-ink-soft">
              Whether you're searching for anxiety counseling, depression support, couples therapy, or grief and life-transition care near {t.town}, you'll be met with steadiness, warmth and zero judgment.
            </p>
            <div className="mt-10 flex flex-col gap-4 sm:flex-row">
              <Link to="/new-patient" data-testid="location-cta" className="rounded-full bg-ink px-8 py-3.5 text-center font-sans text-sm text-linen transition-colors hover:bg-ink/85">Enroll from {t.town}</Link>
              <Link to="/contact" className="rounded-full border border-border px-8 py-3.5 text-center font-sans text-sm text-ink-soft transition-colors hover:bg-white">Directions from {t.town}</Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-24">
        <div className="mx-auto max-w-6xl px-6">
          <div className="overline mb-8">How we help {t.town} residents</div>
          <div className="grid gap-px overflow-hidden border border-border bg-border sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} className="group bg-white p-8 transition-colors hover:bg-linen">
                <h2 className="font-serif text-xl text-ink">{s.short}</h2>
                <p className="mt-2 font-sans text-sm text-ink-soft">{s.tagline}</p>
                <span className="mt-5 inline-flex items-center gap-2 font-sans text-xs text-clay group-hover:translate-x-1">Learn more <ArrowRight size={13} /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>
      <CtaBand />
    </>
  );
}
